import { useState } from 'react';
import Button from '@ui/components/Button/Button';
import Icon from '@ui/components/Icon/Icon';
import useScimTokenQuery from '../../queries/useScimTokenQuery';
import useGenerateScimTokenMutation from '../../queries/useGenerateScimTokenMutation';
import ScimKeyAvailableModal from '../../modals/ScimKeyAvailableModal/ScimKeyAvailableModal';

import './ScimProvisioningCard.scss';

const MASKED_TOKEN = '****************';

const formatTokenDate = ( isoDate: string ) => new Intl.DateTimeFormat(
	'en-US', { month: 'short', day: '2-digit', year: 'numeric' }
).format( new Date( isoDate ) );

type Props = {
	workspaceID: number;
};

const ScimProvisioningCard = ( { workspaceID }: Props ) => {
	const [ plainToken, setPlainToken ] = useState<string | null>( null );
	const [ copiedUrl, setCopiedUrl ] = useState( false );
	const { data: scimToken, isLoading } = useScimTokenQuery( { workspaceID } );
	const { mutate: generateToken, isLoading: isGenerating } = useGenerateScimTokenMutation( {
		workspaceID,
		onSuccess: setPlainToken
	} );

	const handleCopyUrl = () => {
		if ( !scimToken ) return;
		navigator.clipboard.writeText( scimToken.scimUrl );
		setCopiedUrl( true );
		setTimeout( () => setCopiedUrl( false ), 2000 );
	};

	const handleGenerate = () => generateToken();
	const handleCloseModal = () => setPlainToken( null );

	return (
		<div className="ScimProvisioningCard">
			<div className="ScimProvisioningCard-header">
				<h2 className="ScimProvisioningCard-title">SCIM Provisioning</h2>
				<p className="ScimProvisioningCard-description">
					Connect your identity provider to automatically manage VeryBusy accounts when employees join,
					change roles, or leave. Copy your SCIM key and endpoint to your identity provider.{' '}
					{/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
					<a className="ScimProvisioningCard-setup-link" href="#">How to set up SCIM →</a>
				</p>
			</div>

			{!isLoading && (
				<div className="ScimProvisioningCard-rows">
					<div className="ScimProvisioningCard-row">
						<span className="ScimProvisioningCard-row-label">SCIM Base Url</span>
						<div className="ScimProvisioningCard-row-content">
							<span className="ScimProvisioningCard-row-value">{scimToken?.scimUrl}</span>
							<button
								className={`ScimProvisioningCard-copy-btn${copiedUrl ? ' ScimProvisioningCard-copy-btn--copied' : ''}`}
								onClick={handleCopyUrl}
							>
								{copiedUrl ? 'Copied' : 'Copy'}
								{copiedUrl
									? <Icon icon="check" size={16} />
									: (
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M10.6663 0.667969H2.66634C1.93301 0.667969 1.33301 1.26797 1.33301 2.0013V11.3346H2.66634V2.0013H10.6663V0.667969ZM12.6663 3.33464H5.33301C4.59967 3.33464 3.99967 3.93464 3.99967 4.66797V14.0013C3.99967 14.7346 4.59967 15.3346 5.33301 15.3346H12.6663C13.3997 15.3346 13.9997 14.7346 13.9997 14.0013V4.66797C13.9997 3.93464 13.3997 3.33464 12.6663 3.33464ZM12.6663 14.0013H5.33301V4.66797H12.6663V14.0013Z" fill="var(--color-icon-secondary-dark)" />
										</svg>
									)}
							</button>
						</div>
					</div>

					<div className="ScimProvisioningCard-row">
						<span className="ScimProvisioningCard-row-label">Bearer Token</span>
						<div className="ScimProvisioningCard-row-content">
							<span className="ScimProvisioningCard-row-value">{MASKED_TOKEN}</span>
							{scimToken?.active ? (
								<>
									<span className="ScimProvisioningCard-row-date">
										Generated {scimToken.createdAt ? formatTokenDate( scimToken.createdAt ) : ''}
									</span>
									<button
										className="ScimProvisioningCard-rotate-btn"
										onClick={handleGenerate}
										disabled={isGenerating}
									>
										Rotate
										<Icon icon="refresh" size={16} />
									</button>
								</>
							) : (
								<>
									<span className="ScimProvisioningCard-row-date">-</span>
									<Button size="xs" variant="filled" onClick={handleGenerate} loading={isGenerating}>
										Generate Token
									</Button>
								</>
							)}
						</div>
					</div>

					{scimToken?.active && (
						<div className="ScimProvisioningCard-warning">
							<Icon icon="warning" size={16} style={{ flexShrink: 0, marginTop: 2 }} />
							<span>After rotating, update the token in your IdP before the old is revoked.</span>
						</div>
					)}
				</div>
			)}

			{plainToken && (
				<ScimKeyAvailableModal
					token={plainToken}
					onClose={handleCloseModal}
				/>
			)}
		</div>
	);
};

export default ScimProvisioningCard;
